import React, { useContext, useState } from 'react';
import { motion } from 'framer-motion';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Check, Star } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = 'AVxKAogYnRKUMoIOsGLE0RJ9oYEg6G1HIc8J1Qk616PoBEJl6R7agIe4U_Whhlz5N-Y7WruFD4RydnvP';

const SubscriptionPage = () => {
  const { user, token, setShowAuthModal } = useContext(AuthContext);
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);

  const features = [
    'Accès illimité à tous les tutoriels',
    'Nouveau contenu chaque semaine',
    'Tutoriels HD de haute qualité',
    'Techniques de maquillage professionnelles',
    'Styles de coiffure tendance',
    'Support communautaire',
    'Pas de frais cachés',
    'Annulation à tout moment'
  ];

  const handleCreateOrder = async () => {
    if (!token) {
      setShowAuthModal(true);
      throw new Error('Please login first');
    }

    try {
      const response = await axios.post(
        `${API}/payment/create-order`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.order_id;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la commande');
      throw error;
    }
  };

  const handleApprove = async (data) => {
    setProcessing(true);
    try {
      await axios.post(
        `${API}/payment/capture`,
        { order_id: data.orderID },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('🎉 Abonnement activé avec succès!');
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      toast.error('Erreur lors de l\'activation de l\'abonnement');
    } finally {
      setProcessing(false);
    }
  };

  if (user && user.has_subscription) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="min-h-screen flex items-center justify-center pb-24 md:pb-12"
      >
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="inline-flex items-center justify-center w-24 h-24 bg-primary mb-6"
          >
            <Check className="w-12 h-12 text-white" />
          </motion.div>
          <h1 className="font-serif text-5xl text-charcoal tracking-tighter mb-4">
            Vous êtes abonné!
          </h1>
          <p className="text-lg text-taupe mb-8">
            Profitez de tous nos tutoriels premium
          </p>
          <button
            onClick={() => navigate('/browse')}
            data-testid="subscription-browse-btn"
            className="bg-primary text-white hover:bg-primary/90 px-8 py-4 uppercase tracking-widest font-serif text-sm transition-all duration-500"
          >
            Explorer les tutoriels
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pb-24 md:pb-12"
      data-testid="subscription-page"
    >
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center justify-center w-16 h-16 bg-primary mb-6"
              >
                <Star className="w-8 h-8 text-white" />
              </motion.div>
              <h1 className="font-serif text-5xl md:text-6xl text-charcoal tracking-tighter mb-4">
                Abonnement Premium
              </h1>
              <p className="text-xl text-taupe mb-8">
                Un seul paiement pour un accès à vie
              </p>
              <div className="inline-block">
                <div className="flex items-baseline gap-2">
                  <span className="font-serif text-7xl text-secondary">$9.29</span>
                  <span className="text-xl text-taupe">à vie</span>
                </div>
                <p className="text-sm text-taupe uppercase tracking-wider mt-2">Paiement unique</p>
              </div>
            </div>

            {/* Features Grid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12"
            >
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-4 p-6 bg-silk border gold-border">
                  <Check className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                  <span className="text-charcoal">{feature}</span>
                </div>
              ))}
            </motion.div>

            {/* PayPal Payment */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-silk p-8 md:p-12 border gold-border"
            >
              <h3 className="font-serif text-3xl text-charcoal tracking-tighter mb-6 text-center">
                Finalisez votre abonnement
              </h3>
              
              {!user ? (
                <div className="text-center" data-testid="subscription-login-prompt">
                  <p className="text-taupe mb-6">Vous devez être connecté pour vous abonner</p>
                  <button
                    onClick={() => setShowAuthModal(true)}
                    data-testid="subscription-login-btn"
                    className="bg-primary text-white hover:bg-primary/90 px-8 py-4 uppercase tracking-widest font-serif text-sm transition-all duration-500"
                  >
                    Se connecter
                  </button>
                </div>
              ) : (
                <div data-testid="paypal-buttons-container">
                  <PayPalScriptProvider
                    options={{
                      clientId: PAYPAL_CLIENT_ID,
                      currency: 'USD',
                      intent: 'capture'
                    }}
                  >
                    <PayPalButtons
                      style={{
                        layout: 'vertical',
                        color: 'gold',
                        shape: 'rect',
                        label: 'pay'
                      }}
                      createOrder={handleCreateOrder}
                      onApprove={handleApprove}
                      onError={(err) => {
                        console.error('PayPal Error:', err);
                        toast.error('Erreur lors du paiement');
                      }}
                      disabled={processing}
                    />
                  </PayPalScriptProvider>
                </div>
              )}

              <div className="mt-8 pt-8 border-t gold-border text-center">
                <p className="text-sm text-taupe">
                  Paiement sécurisé par PayPal. Vos informations sont protégées.
                </p>
              </div>
            </motion.div>

            {/* Guarantee */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-center mt-12 p-8 bg-primary/5"
            >
              <p className="font-accent text-sm uppercase tracking-wider text-primary mb-2">
                Garantie Satisfaction
              </p>
              <p className="text-charcoal">
                Rejoignez des milliers de femmes satisfaites qui ont transformé leur routine beauté
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default SubscriptionPage;